import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg';
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconChevronRight } from '@/assets/svgs/chevron-right.svg';
import { ReactComponent as IconNoUser } from '@/assets/svgs/no-user-added.svg';
import Button from '@/components/Button/index';
import DatePicker from '@/components/DatePicker/index';
import LoadingPortal from '@/components/LoadingPortal/index';
// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';
import Breadcrumb from '@/dsComponents/Breadcrumb';
import Search from '@/componentsDS/Search/index';
import { featureConstant } from '@/constants/configuration/feature';
import { useTradex } from '@/hooks/use-tradex/index';
import useDebounce from '@/hooks/use-debounce/index';
import useOrg from '@/hooks/use-org/index';
import usePage from '@/hooks/use-page';
import useUseCases from '@/hooks/use-use-cases/index';
import { useNavigate } from '@/lib/router-dom';
import { feature } from '@/utils/feature-verifier';
import OverViewFilterSelect from '@/dsComponents/select/OverViewFilterSelect';
import { produce } from 'immer';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Box } from '@dextech-ai/dex-design-system';

const Page = () => {
	/*=================================================1
		General
	/================================================*/


	const isTradex = useTradex();

	const [pageTitle, setPageTitle] = useState("Participants");
	const [currentPage, setCurrentPage] = useState(1)

	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)

	// const [useCases, setUseCases] = useState([])

	const [searchParticipant, setSearchParticipant] = useState()

	const handleChange =(e) =>{
		setSearchParticipant(e.target.value)
	}
	const handleCancel =()=> setSearchParticipant('')

	const pageLimit = 10
	const [sortConfig, setSortConfig] = useState({ sortBy: 'created', direction: false })

	const { goTo } = useNavigate()

	/*=================================================
	API
	/================================================*/

	/**
	 * filterUseCase state is collected mainly to get
	 * initial useCaseList that collected from backend.
	 * On initialMount, getFilterUseCase will get called.
	 **/
	const {
		filterUseCase,
		getFilterUseCase: fetchUseCases,
		loading
	} = useUseCases();

	/**
	 * The useCases state will have empty array at the beginning.
	 * Once the filtered use case list is fetched data from the backend
	 * then it will sync with useEffect.
	 */
	const [useCases, setUseCases] = useState([]);

	const { items: orgList, fetch: fetchOrgList, setItems: setOrgList, allData: entireOrgData } = useOrg()

	const debouncedFetchSortedData = useDebounce(getSortedData, "dataFiltering");

	/* cDM: Fetching required data from backend */
	useEffect(() => {
		// fetch()
		// fetchOrgList();
		fetchUseCases();
	}, []);

	/* cDU: Syncing filterUseCase with useCases State */
	useEffect(() => {
		setUseCases(filterUseCase);
	}, [filterUseCase])


	useEffect(() => {
		getSortedData();
	}, [startDate, endDate, useCases, sortConfig, currentPage])


	/*=================================================
		Routes
	/================================================*/

	function handleDataRequestView(item) {
		goTo(`/admin/participants/dashboard`, {data: item});
	}

	/*=================================================
		Sort
	/================================================*/
	function handleSort(sortBy, type) {
		let direction = sortConfig.direction
		if (sortConfig.sortBy === sortBy) {
			direction = !direction
		} else {
			direction = true
		}

		const sortDirection = direction === true ? "asc" : "desc";

		setSortConfig({ sortBy, direction })
	}

	function getSortedData() {
		fetchOrgList(
			{
				page: currentPage,
				count: pageLimit,
				start: startDate !== null ? moment(startDate).format('yyyy-MM-DD HH:mm') : null,
				end: endDate !== null ? moment(endDate).format('yyyy-MM-DD HH:mm') : null,
				orderBy: sortConfig.sortBy,
				direction: sortConfig.direction === true ? "asc" : "desc",
				usecaseId: convertArrayToString(useCases) ?? null,
				name: searchParticipant
			}
		);
	}

	/*=================================================
		Search
	/================================================*/

	useEffect(() => {
		debouncedFetchSortedData();
	}, [searchParticipant])

	function clearFilter() {
		function setFalse(array) {
			const updatedItems = produce(array, draft => {
				draft.forEach(
					item => item.$selected = false
				)
			})
			return updatedItems
		}

		setSearchParticipant(null)
		setStartDate(null)
		setEndDate(null)
		setUseCases(setFalse((useCases)))
	}

	/*=================================================
	Pagination
	/================================================*/

	function goToPrev() {

		if (currentPage === 1) {
			return
		}

		setCurrentPage(prev => prev - 1)
	}

	function goToNext() {
		if (currentPage === entireOrgData?.meta?.totalPages) {
			return
		}

		setCurrentPage(prev => prev + 1)
	}


	useEffect(() => {
		if (currentPage > entireOrgData?.meta?.totalPages) {
			setCurrentPage(entireOrgData?.meta?.totalPages || 1)
		}
	}, [orgList])


	function convertArrayToString(array) {

		if (!Array.isArray(array)) {
			return null
		}

		if (array.every(item => item.$selected === true)) {
			return null
		}

		const tempArray = retrieveId(array)
		if (tempArray.length > 0) {
			return tempArray.toString();
		}

		return null
	}

	function retrieveId(array) {
		const tempArray = [];

		array.forEach(item => {
			if (item.$selected) {
				tempArray.push(item.id)
			}
		})

		return tempArray
	}

	/*=================================================
		Data Table
	/================================================*/
	const DataTable = ({ itemData, type }) => {

		function getClassName(name) {
			if (!sortConfig) {
				return
			}
			return sortConfig.sortBy === name ? (sortConfig.direction === true ? "is-asc" : "is-desc") : ""
		}

		const list = itemData?.map((item, index) => {
			return (
				<div className="data-table__table__body__row" key={index}>

					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--break data-table__flex">
						<div className='data-table---icon-wrapper' style={{ marginRight: 10 }}>
							<div className='data-table---avatar' style={{ backgroundImage: `url(${item?.iconUrl})` }}></div>
						</div>
						{item?.name}
					</div>

					<div className="data-table__table__body__content data-table__table__body__content--text">
						{moment(item?.created).format('D MMM YYYY')}
					</div>

					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break">
						{item?.domain}
					</div>

					{isTradex && <div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break">
						{item?.enrolments}
					</div>}

					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break">
						{item?.dataElements}
					</div>

					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--end">
						<span data-testid="view-btn" className="data-table__table__body__content__action link"
							onClick={() => {
								handleDataRequestView(item)
							}}
						>
							<IconChevronRight />
						</span>
					</div>
				</div>
			)
		})


		function handleCurrnetInput(e) {
			const value = e.target?.value === "" ? 1 : e.target.value
			const current = parseInt(value)

			if (current < 1) {
				return setCurrentPage(1)
			}

			// if(current > auditMeta.totalPages){
			// 	return setCurrentPage(auditMeta.totalPages)
			// }

			setCurrentPage(current)
		}

		const NoMessage = () => {
			return (
				<div className="data-table__empty__icon">
					<IconNoUser />
					<span>No Participant found</span>
				</div>
			)
		}

		return (
			<div className="data-table__content">
				<div className="data-table__content__cols">
					<div className="data-table__content__col">
						<div className="data-table__table">
							<div className="data-table__table__head">
								<div className="data-table__table__head__row">
									<div className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("name")}`}
										id="name" onClick={(e) => { handleSort('name', type) }}>
										Participant Name
										<span className="data-table__table__head__icon__sort-order"></span>
									</div>

									<div className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("created")}`}
										id="created" onClick={(e) => { handleSort('created', type) }}>
										Date Joined <span className="data-table__table__head__icon__sort-order"></span>
									</div>

									<div className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("domain")}`}
										id="domain" onClick={(e) => { handleSort('domain', type) }}>
										Domain <span className="data-table__table__head__icon__sort-order"></span>
									</div>

									{isTradex && <div className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("uen")}`}
										id="uen" onClick={(e) => { handleSort('uen', type) }}>
										Use cases enrolled
										<span className="data-table__table__head__icon__sort-order"></span>
									</div>}

									<div className={`data-table__table__head__title  data-table__table__head__title__sort-order ${getClassName("dataElements")}`}
										id="dataElements" onClick={(e) => { handleSort('dataElements', type) }}>
										Data elements exchanged
										<span className="data-table__table__head__icon__sort-order"></span>
									</div>

									<div className="data-table__table__head__title data-table__header data-table__table__head__title--end">
									</div>

								</div>
							</div>

							{list?.length > 0 ?
								<div className="data-table__table__body">
									{
										list
									}
								</div>
								:

								<div className="data-table__empty"><NoMessage /></div>
							}

							{
								list?.length > 0 && (
									<div className="data-table__table__footer">
										<div className="data-table__table__footer__row">
											<div className="data-table__table__footer__row__left">
												<div className="data-table__table__pagination">
													<input data-testid="pagination-input" className="data-table__table__pagination__page" type="number" value={currentPage} onChange={(e) => handleCurrnetInput(e)} min="1" />
													<span className="data-table__table__pagination__page__meta"> of {entireOrgData?.meta?.totalPages || 1} pages </span>
													<button data-testid="pagination-prev" className="data-table__table__pagination__prev" onClick={() => goToPrev()} disabled={currentPage === 1}><IconChevronDown /></button>
													<button data-testid="pagination-next" className="data-table__table__pagination__next" onClick={() => goToNext()} disabled={currentPage === entireOrgData?.meta?.totalPages}><IconChevronDown /></button>
												</div>
											</div>
											<div className="data-table__table__footer__row__right">
												<div className="data-table__table__meta">
													Showing {orgList?.length} entries
												</div>
											</div>
										</div>
									</div>
								)
							}
						</div>
					</div>
				</div>
			</div>
		)
	}


	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
				isHomePage: true
			},
			{
				label: pageTitle,
				link: ``
			},
		]
	})
	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<div className="title-header__right">
					<div className="overview__filter overview__button_wrap">
						<Button round full maxWidth={140} text="Add New User" />
					</div>
				</div>
			</div>

			{/* <hr /> */}
			<div className="overview">
				<div className="overview_filter">
					<div className="overview_filter__cols">
						<div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Participant Name
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								{feature(featureConstant.NEW_DESIGN_SYSTEM) ?
									<Search value={searchParticipant || ''} placeholder="Enter participant name" onChange={handleChange} onCancel={handleCancel} />
									:
									<input type="text"
										value={searchParticipant || ''}
										onChange={(e) => setSearchParticipant(e.target.value)}
										placeholder="Enter participant name"
										className="data-table__table__search"
										autoFocus="autoFoucs"
									/>
								}
							</div>
						</div>

						<div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Joined start date
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<DatePicker
									selectDate={startDate}
									setSelectDate={setStartDate}
									placeHolderText="Pick date and time"

								/>
							</div>
						</div>
						<div className="overview_filter__col">
							<Box mb={2}>
								&nbsp;
							</Box>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--padding-top-bottom ">
								–
							</div>
						</div>
						<div className="overview_filter__col">
							<Box mb={2}>
								Joined end date
							</Box>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<DatePicker
									selectDate={endDate}
									setSelectDate={setEndDate}
									placeHolderText="Pick date and time"
								/>
							</div>
						</div>
						{isTradex && <div className="overview_filter__col">
							<div className="overview_filter__col__top overview_filter__col__title">
								Use case
							</div>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<OverViewFilterSelect
									data-testid="participants-use-case-filter"
									items={useCases}
									onChange={setUseCases}
								/>
							</div>
						</div>}
						<div className="overview_filter__col">
							<Box mb={2}>
								&nbsp;
							</Box>
							<div className="overview_filter__col__bottom overview_filter__col__bottom--background">
								<Button
									text="Clear filters"
									ghost
									submit={() => clearFilter()}
								/>
							</div>
						</div>

					</div>
				</div>
				<Box minH="540px" mt={4}>
					<DataTable itemData={orgList} />
				</Box>
			</div>

			<LoadingPortal isLoading={loading} />



		</>
	)
}

export default Page
